import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { gymsService } from '../../services/gyms';
import LocationHeader from '../../components/location/LocationHeader';
import { GymInfoFields, type GymInfoValues } from '../../components/gym/GymInfoFields';
import { useGymAdmin } from '../context/gym-admin-context';
import { useCurrentGym } from '../hooks/useCurrentGym';
import CragInfoPage from './crag-info';
import TrophyInfoPage from './trophy-info';
import CompetitionInfoPage from './competition-info';
import type { GalleryImage } from '../../types/location-images';

const EMPTY_INFO: GymInfoValues = {
  websiteUrl: '',
  address: '',
  city: '',
  zipcode: '',
  latitude: '',
  longitude: '',
};

/**
 * The Info section, whichever kind of place is selected. Every variant lives
 * behind one route so switching places in the sidebar keeps the reader where
 * they were instead of bouncing them to a redirect.
 */
export default function InfoPage() {
  const { locationType } = useGymAdmin();

  switch (locationType) {
    case 'crag':
      return <CragInfoPage />;
    case 'trophy':
      return <TrophyInfoPage />;
    case 'competition':
      return <CompetitionInfoPage />;
    default:
      return <GymInfoPage />;
  }
}

/**
 * Admin-only gym profile: logo, cover & gallery images and short description are
 * edited inline through LocationHeader (which handles ActiveStorage uploads),
 * while the contact/location fields are saved via gymsService.updateGym.
 */
function GymInfoPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gym, loading, setGym, slug } = useCurrentGym();

  const [info, setInfo] = useState<GymInfoValues>(EMPTY_INFO);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!gym) return;
    setInfo({
      websiteUrl: gym.website_url || '',
      address: gym.address || '',
      city: gym.city || '',
      zipcode: gym.zipcode || '',
      latitude: gym.latitude != null ? String(gym.latitude) : '',
      longitude: gym.longitude != null ? String(gym.longitude) : '',
    });
  }, [gym]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await gymsService.updateGym(slug, {
        website_url: info.websiteUrl || undefined,
        address: info.address || undefined,
        city: info.city || undefined,
        zipcode: info.zipcode || undefined,
        latitude: info.latitude ? parseFloat(info.latitude) : undefined,
        longitude: info.longitude ? parseFloat(info.longitude) : undefined,
      });
      setGym(updated);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!gym) return null;

  return (
    <div className="max-w-4xl space-y-6">
      {/* Logo, cover, gallery and short description (bio) */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.imagesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <LocationHeader
            locationId={gym.id}
            name={gym.name}
            bio={gym.bio}
            coverImageUrl={gym.cover_image_url}
            logoUrl={gym.logo_url}
            logoSmallUrl={gym.logo_small_url}
            galleryImages={gym.gallery_images ?? []}
            fallbackIcon={<Building2 className="w-8 h-8 text-muted-foreground" />}
            isStaffMember
            isStaffAdmin
            hideName
            onBioUpdated={(bio) => setGym((g) => (g ? { ...g, bio } : g))}
            onCoverUpdated={(url) => setGym((g) => (g ? { ...g, cover_image_url: url } : g))}
            onLogoUpdated={(url, smallUrl) =>
              setGym((g) => (g ? { ...g, logo_url: url, logo_small_url: smallUrl } : g))
            }
            onGalleryUpdated={(imgs: GalleryImage[]) =>
              setGym((g) => (g ? { ...g, gallery_images: imgs } : g))
            }
          />
        </CardContent>
      </Card>

      {/* Contact & location */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymAdmin.info.detailsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <GymInfoFields values={info} onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))} />
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
